import { getRpcAuthToken, setRpcToken } from '../api/rpc-client';

const PROTECTED_AUTH_TOKEN_STORAGE_KEY = 'grato.auth.protected-token';
const AUTH_TOKEN_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const AUTH_TOKEN_ALPHABET_LENGTH = AUTH_TOKEN_ALPHABET.length;
const AUTH_TOKEN_PIN_PATTERN = /^\d{4}$/;
const AUTH_TOKEN_PLAINTEXT_PATTERN = /^[A-Za-z0-9]+$/;
const PBKDF2_ITERATIONS = 60000;
const AES_KEY_LENGTH_BITS = 256;
const AES_CTR_COUNTER_BITS = 64;
const SALT_BYTES = 16;
const COUNTER_BYTES = 16;
const PROTECTED_AUTH_TOKEN_VERSION = 1;

export type AuthTokenStorageStatus = 'anonymous' | 'authenticated' | 'locked';

type ProtectedAuthTokenRecord = {
  counter: string;
  encryptedToken: string;
  salt: string;
  version: number;
};

type AuthTokenStorageErrorCode =
  | 'crypto-unavailable'
  | 'invalid-pin-format'
  | 'missing-protected-token'
  | 'missing-runtime-token'
  | 'storage-unavailable'
  | 'unsupported-token-format';

export class AuthTokenStorageError extends Error {
  readonly code: AuthTokenStorageErrorCode;

  constructor(code: AuthTokenStorageErrorCode) {
    super(code);
    this.name = 'AuthTokenStorageError';
    this.code = code;
  }
}

function canUseLocalStorage() {
  return (
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  );
}

function resolveCryptoApi(): Crypto {
  if (typeof globalThis.crypto === 'undefined' || !globalThis.crypto.subtle) {
    throw new AuthTokenStorageError('crypto-unavailable');
  }

  return globalThis.crypto;
}

function assertValidPin(pin: string) {
  if (!AUTH_TOKEN_PIN_PATTERN.test(pin)) {
    throw new AuthTokenStorageError('invalid-pin-format');
  }
}

function assertProtectableAuthToken(token: string) {
  if (!AUTH_TOKEN_PLAINTEXT_PATTERN.test(token)) {
    throw new AuthTokenStorageError('unsupported-token-format');
  }
}

function clearProtectedAuthTokenRecord(): void {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(PROTECTED_AUTH_TOKEN_STORAGE_KEY);
  } catch {
    // localStorage can be blocked in privacy mode.
  }
}

function isHex(value: string): boolean {
  return (
    value.length > 0 && value.length % 2 === 0 && /^[0-9a-f]+$/i.test(value)
  );
}

function isProtectedAuthTokenRecord(
  value: unknown,
): value is ProtectedAuthTokenRecord {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Partial<ProtectedAuthTokenRecord>;

  return (
    record.version === PROTECTED_AUTH_TOKEN_VERSION &&
    typeof record.encryptedToken === 'string' &&
    AUTH_TOKEN_PLAINTEXT_PATTERN.test(record.encryptedToken) &&
    typeof record.salt === 'string' &&
    isHex(record.salt) &&
    typeof record.counter === 'string' &&
    isHex(record.counter)
  );
}

function readProtectedAuthTokenRecord(): ProtectedAuthTokenRecord | null {
  if (!canUseLocalStorage()) {
    return null;
  }

  try {
    const storedRecord = window.localStorage.getItem(
      PROTECTED_AUTH_TOKEN_STORAGE_KEY,
    );

    if (storedRecord === null) {
      return null;
    }

    const parsedRecord = JSON.parse(storedRecord) as unknown;

    if (!isProtectedAuthTokenRecord(parsedRecord)) {
      clearProtectedAuthTokenRecord();
      return null;
    }

    return parsedRecord;
  } catch {
    clearProtectedAuthTokenRecord();
    return null;
  }
}

function writeProtectedAuthTokenRecord(record: ProtectedAuthTokenRecord): void {
  if (!canUseLocalStorage()) {
    throw new AuthTokenStorageError('storage-unavailable');
  }

  try {
    window.localStorage.setItem(
      PROTECTED_AUTH_TOKEN_STORAGE_KEY,
      JSON.stringify(record),
    );
  } catch {
    throw new AuthTokenStorageError('storage-unavailable');
  }
}

function bytesToHex(bytes: ArrayBuffer | Uint8Array): string {
  return Array.from(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);

  for (let index = 0; index < hex.length; index += 2) {
    bytes[index / 2] = Number.parseInt(hex.slice(index, index + 2), 16);
  }

  return bytes;
}

function resolveAuthTokenAlphabetIndex(character: string): number {
  const index = AUTH_TOKEN_ALPHABET.indexOf(character);

  if (index === -1) {
    throw new AuthTokenStorageError('unsupported-token-format');
  }

  return index;
}

async function deriveAuthTokenKey(
  pin: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const cryptoApi = resolveCryptoApi();
  const baseKey = await cryptoApi.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return cryptoApi.subtle.deriveKey(
    {
      hash: 'SHA-256',
      iterations: PBKDF2_ITERATIONS,
      name: 'PBKDF2',
      salt,
    },
    baseKey,
    {
      length: AES_KEY_LENGTH_BITS,
      name: 'AES-CTR',
    },
    false,
    ['encrypt'],
  );
}

async function resolveKeyStream(
  pin: string,
  salt: Uint8Array,
  counter: Uint8Array,
  length: number,
): Promise<Uint8Array> {
  const cryptoApi = resolveCryptoApi();
  const key = await deriveAuthTokenKey(pin, salt);
  const zeroBuffer = new Uint8Array(length);
  const keyStream = await cryptoApi.subtle.encrypt(
    {
      counter,
      length: AES_CTR_COUNTER_BITS,
      name: 'AES-CTR',
    },
    key,
    zeroBuffer,
  );

  return new Uint8Array(keyStream);
}

async function encryptAuthToken(
  token: string,
  pin: string,
): Promise<ProtectedAuthTokenRecord> {
  assertValidPin(pin);
  assertProtectableAuthToken(token);

  const cryptoApi = resolveCryptoApi();
  const salt = cryptoApi.getRandomValues(new Uint8Array(SALT_BYTES));
  const counter = cryptoApi.getRandomValues(new Uint8Array(COUNTER_BYTES));
  const keyStream = await resolveKeyStream(pin, salt, counter, token.length);
  const encryptedToken = Array.from(token, (character, index) => {
    const sourceIndex = resolveAuthTokenAlphabetIndex(character);
    const shift = keyStream[index] % AUTH_TOKEN_ALPHABET_LENGTH;

    return AUTH_TOKEN_ALPHABET[
      (sourceIndex + shift) % AUTH_TOKEN_ALPHABET_LENGTH
    ];
  }).join('');

  return {
    counter: bytesToHex(counter),
    encryptedToken,
    salt: bytesToHex(salt),
    version: PROTECTED_AUTH_TOKEN_VERSION,
  };
}

async function decryptAuthTokenRecord(
  record: ProtectedAuthTokenRecord,
  pin: string,
): Promise<string> {
  assertValidPin(pin);

  const salt = hexToBytes(record.salt);
  const counter = hexToBytes(record.counter);
  const keyStream = await resolveKeyStream(
    pin,
    salt,
    counter,
    record.encryptedToken.length,
  );

  return Array.from(record.encryptedToken, (character, index) => {
    const encryptedIndex = resolveAuthTokenAlphabetIndex(character);
    const shift = keyStream[index] % AUTH_TOKEN_ALPHABET_LENGTH;
    const decryptedIndex =
      (encryptedIndex - shift + AUTH_TOKEN_ALPHABET_LENGTH) %
      AUTH_TOKEN_ALPHABET_LENGTH;

    return AUTH_TOKEN_ALPHABET[decryptedIndex];
  }).join('');
}

export function writeAuthToken(token: string): void {
  setRpcToken(token);
}

export function readAuthToken(): string | null {
  return getRpcAuthToken();
}

export function hasProtectedAuthToken(): boolean {
  return readProtectedAuthTokenRecord() !== null;
}

export function restoreAuthToken(): AuthTokenStorageStatus {
  if (readAuthToken() !== null) {
    return 'authenticated';
  }

  if (hasProtectedAuthToken()) {
    lockAuthToken();
    return 'locked';
  }

  return 'anonymous';
}

export async function saveAuthTokenPin(pin: string): Promise<void> {
  const token = readAuthToken();

  if (token === null) {
    throw new AuthTokenStorageError('missing-runtime-token');
  }

  const protectedRecord = await encryptAuthToken(token, pin);
  writeProtectedAuthTokenRecord(protectedRecord);
}

export async function changeAuthTokenPin(
  currentPin: string,
  nextPin: string,
): Promise<boolean> {
  const protectedRecord = readProtectedAuthTokenRecord();

  if (protectedRecord === null) {
    await saveAuthTokenPin(nextPin);
    return true;
  }

  const token = readAuthToken();

  if (token === null) {
    throw new AuthTokenStorageError('missing-runtime-token');
  }

  const decryptedToken = await decryptAuthTokenRecord(
    protectedRecord,
    currentPin,
  );

  if (decryptedToken !== token) {
    return false;
  }

  const nextProtectedRecord = await encryptAuthToken(token, nextPin);
  writeProtectedAuthTokenRecord(nextProtectedRecord);

  return true;
}

export async function verifyAuthTokenPin(pin: string): Promise<boolean> {
  const protectedRecord = readProtectedAuthTokenRecord();

  if (protectedRecord === null) {
    throw new AuthTokenStorageError('missing-protected-token');
  }

  const token = readAuthToken();

  if (token === null) {
    throw new AuthTokenStorageError('missing-runtime-token');
  }

  const decryptedToken = await decryptAuthTokenRecord(protectedRecord, pin);

  return decryptedToken === token;
}

export async function unlockAuthToken(pin: string): Promise<string> {
  const protectedRecord = readProtectedAuthTokenRecord();

  if (protectedRecord === null) {
    throw new AuthTokenStorageError('missing-protected-token');
  }

  const decryptedToken = await decryptAuthTokenRecord(protectedRecord, pin);

  writeAuthToken(decryptedToken);

  return decryptedToken;
}

export function lockAuthToken(): void {
  setRpcToken(null);
}

export function clearAuthToken(): void {
  lockAuthToken();
  clearProtectedAuthTokenRecord();
}
